import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Drawer, Typography, IconButton, Button, Tooltip, Divider, CircularProgress,
  Select, MenuItem,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import {
  listCustomerColours, upsertCustomerColour, deleteCustomerColour, CustomerColour,
} from '../productionApi';
import { productionKeys } from '../queryKeys';
import { barStyle, JOB_BAG_COLOURS, jobBagNameForFill } from './barStyle';
import { TOKENS } from './ganttConfig';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Customer codes currently visible on the board, so unmapped ones can be added. */
  customerCodes: string[];
}

interface Draft {
  fill: string;
  text: string;
  border: string | null;
}

/**
 * ColourSettingsDrawer — edit the per-customer Press Schedule Board colours that
 * back `barStyle()`. Saving upserts production.customer_colours and invalidates
 * the colour query so the board (bars + cards + parked chips) recolours live.
 * Reset deletes the row, reverting the customer to the forme-hash fallback.
 */
export default function ColourSettingsDrawer({ open, onClose, customerCodes }: Props) {
  const qc = useQueryClient();
  const coloursQ = useQuery({
    queryKey: productionKeys.customerColours(),
    queryFn: listCustomerColours,
  });

  // Local edits, keyed by customer code. Seeded lazily from the fetched rows.
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});

  const rowsByCode = useMemo(() => {
    const m: Record<string, CustomerColour> = {};
    for (const r of coloursQ.data ?? []) m[r.customer_code] = r;
    return m;
  }, [coloursQ.data]);

  // Union of saved customers and those seen on the board, sorted.
  const codes = useMemo(() => {
    const set = new Set<string>(customerCodes.filter(Boolean));
    for (const r of coloursQ.data ?? []) set.add(r.customer_code);
    return Array.from(set).sort();
  }, [customerCodes, coloursQ.data]);

  const draftFor = (code: string): Draft => {
    if (drafts[code]) return drafts[code];
    const row = rowsByCode[code];
    if (row) return { fill: row.fill, text: row.text, border: row.border };
    // No saved row yet — preview the fallback so editing starts from what's shown.
    const fb = barStyle(code, null, {});
    return { fill: fb.fill, text: fb.text, border: fb.border ?? null };
  };

  const setDraft = (code: string, patch: Partial<Draft>) =>
    setDrafts((prev) => ({ ...prev, [code]: { ...draftFor(code), ...patch } }));

  const invalidate = () => qc.invalidateQueries({ queryKey: productionKeys.customerColours() });

  const saveM = useMutation({
    mutationFn: (vars: { code: string } & Draft) =>
      upsertCustomerColour({ customer_code: vars.code, fill: vars.fill, text: vars.text, border: vars.border }),
    onSuccess: (_data, vars) => {
      setDrafts((prev) => { const n = { ...prev }; delete n[vars.code]; return n; });
      invalidate();
    },
  });

  const resetM = useMutation({
    mutationFn: (code: string) => deleteCustomerColour(code),
    onSuccess: (_data, code) => {
      setDrafts((prev) => { const n = { ...prev }; delete n[code]; return n; });
      invalidate();
    },
  });

  const isDirty = (code: string): boolean => {
    const d = drafts[code];
    if (!d) return false;
    const row = rowsByCode[code];
    if (!row) return true;
    return d.fill !== row.fill || d.text !== row.text || (d.border ?? null) !== (row.border ?? null);
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: 380, maxWidth: '90vw' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5, borderBottom: `1px solid ${TOKENS.line}` }}>
        <Typography sx={{ fontWeight: 700, fontSize: 16, flex: 1 }}>Customer colours</Typography>
        <IconButton size="small" onClick={onClose} aria-label="Close"><CloseRoundedIcon fontSize="small" /></IconButton>
      </Box>
      <Box sx={{ px: 2, py: 1, fontSize: 12, color: TOKENS.ink3 }}>
        Pick a standard job-bag colour for each customer, then Save. Customers without one
        use an automatic per-forme palette. Changes apply to the board immediately.
      </Box>
      <Divider />

      {coloursQ.isLoading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}><CircularProgress size={24} /></Box>
      ) : (
        <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {codes.map((code) => {
            const d = draftFor(code);
            const mapped = !!rowsByCode[code];
            const dirty = isDirty(code);
            const busy = (saveM.isPending && saveM.variables?.code === code)
              || (resetM.isPending && resetM.variables === code);
            return (
              <Box key={code} sx={{
                display: 'flex', alignItems: 'center', gap: 1, p: 1,
                border: `1px solid ${TOKENS.line}`, borderRadius: '10px', background: TOKENS.panel,
              }}>
                {/* live preview chip */}
                <Box sx={{
                  minWidth: 56, textAlign: 'center', px: 1, py: 0.75, borderRadius: '8px',
                  fontSize: 12, fontWeight: 700, background: d.fill, color: d.text,
                  border: d.border ? `1px solid ${d.border}` : '1px solid transparent',
                }}>{code}</Box>

                <Box sx={{ flex: 1 }}>
                  <Select
                    size="small" fullWidth displayEmpty
                    value={jobBagNameForFill(d.fill) ?? ''}
                    onChange={(e) => {
                      const c = JOB_BAG_COLOURS.find((x) => x.name === e.target.value);
                      if (c) setDraft(code, { fill: c.fill, text: c.text, border: c.border });
                    }}
                    renderValue={(val) => (val ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 14, height: 14, borderRadius: '3px', background: d.fill, border: `1px solid ${d.border ?? 'rgba(0,0,0,.2)'}` }} />
                        {val}
                      </Box>
                    ) : (
                      <span style={{ color: TOKENS.ink3 }}>Choose colour…</span>
                    ))}
                    sx={{ fontSize: 13, '& .MuiSelect-select': { py: 0.75 } }}
                  >
                    {JOB_BAG_COLOURS.map((c) => (
                      <MenuItem key={c.name} value={c.name} sx={{ fontSize: 13, gap: 1 }}>
                        <Box sx={{ width: 14, height: 14, borderRadius: '3px', background: c.fill, border: `1px solid ${c.border ?? 'rgba(0,0,0,.2)'}` }} />
                        {c.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  <Button size="small" variant="contained" disabled={!dirty || busy}
                    onClick={() => saveM.mutate({ code, ...d })}
                    sx={{ textTransform: 'none', fontSize: 12, minWidth: 0, px: 1.25 }}>
                    Save
                  </Button>
                  {mapped && (
                    <Tooltip title="Reset to automatic colour" arrow>
                      <span>
                        <IconButton size="small" disabled={busy} onClick={() => resetM.mutate(code)}
                          aria-label={`Reset ${code}`}>
                          <RestartAltRoundedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            );
          })}
          {codes.length === 0 && (
            <Box sx={{ py: 4, textAlign: 'center', fontSize: 12, color: TOKENS.ink3 }}>
              No customers on the board yet.
            </Box>
          )}
        </Box>
      )}
    </Drawer>
  );
}
